import styles from "./styles.module.scss";
import { useContext, useState } from "react";
import { useMediaQuery } from "usehooks-ts";
import { BottomNavigation, BottomNavigationAction, Paper } from "@mui/material";
import MenuIcon from "assets/icons/menu2.svg?react";
import ModalsManagerContext from "contexts/ModalsManager";
import DeskPlanner from "./DeskPlanner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFolderOpen,
  faFile,
  faLink,
  faEnvelope,
  faStickyNote,
  faComments,
  faBell,
  faThumbtack,
  faClock,
  faLock,
  faCalendarDays,
  faExpand,
  faCompress,
  faPalette,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

// Maski (motywy) Biurka — wybierane przez użytkownika, zapamiętywane w localStorage.
const MASK_STORAGE_KEY = "desk_mask_v1";
interface MaskDef {
  key: string;
  name: string;
  desc: string;
  swatch: string[];
}
const MASKS: MaskDef[] = [
  {
    key: "minimal",
    name: "Minimalistyczna",
    desc: "Monochromatyczna, proste kafle bez zaokrągleń",
    swatch: ["#ffffff", "#e4e4e7", "#a1a1aa", "#3f3f46"],
  },
  {
    key: "current",
    name: "Klasyczna",
    desc: "Obecny wygląd biurka — jasny, z akcentami koloru",
    swatch: ["#5d49c7", "#1565c0", "#e6a100", "#c2255c"],
  },
  {
    key: "vivid",
    name: "Żywa",
    desc: "Więcej koloru i energii, wyraźniejsze kafle",
    swatch: ["#7c3aed", "#06b6d4", "#f59e0b", "#ec4899"],
  },
  {
    key: "onyx",
    name: "Onyks — autorska",
    desc: "Elegancki, ciemny motyw ze szklanymi kaflami",
    swatch: ["#16131f", "#5d49c7", "#2dd4bf", "#e7e3f5"],
  },
];

/**
 * Biurko — Etap 1 (autorska propozycja wyglądu). Układ „bento": pogrupowane karty
 * z własnym kolorem akcentu i ikoną. Zawartość to mockup (przykładowe kafelki) —
 * realne dane i reguły umieszczania (📌 przypięte + ⏱ ostatnio używane) podłączamy
 * w Etapie 2. Grupy: Teczki/pliki/linki · Przesyłki · Notatki · Czat · Powiadomienia.
 */

type Reason = "pin" | "recent";

interface SampleItem {
  icon: IconDefinition;
  title: string;
  sub?: string;
  reason: Reason;
  when?: string;
  badge?: string;
}

const ReasonChip = ({ reason, when }: { reason: Reason; when?: string }) =>
  reason === "pin" ? (
    <span className={`${styles.chip} ${styles.chipPin}`}>
      <FontAwesomeIcon icon={faThumbtack} /> przypięte
    </span>
  ) : (
    <span className={`${styles.chip} ${styles.chipRecent}`}>
      <FontAwesomeIcon icon={faClock} /> {when ?? "ostatnio"}
    </span>
  );

const ItemTile = ({ item }: { item: SampleItem }) => (
  <div className={styles.tile}>
    <span className={styles.tileIcon}>
      <FontAwesomeIcon icon={item.icon} />
    </span>
    <span className={styles.tileText}>
      <span className={styles.tileTitle}>{item.title}</span>
      {item.sub && <span className={styles.tileSub}>{item.sub}</span>}
    </span>
    {item.badge && <span className={styles.tileBadge}>{item.badge}</span>}
    <ReasonChip reason={item.reason} when={item.when} />
  </div>
);

// Wiek pozycji w minutach (z opisu „when"), do sortowania chronologicznego.
const ageMinOf = (item: SampleItem): number => {
  const w = item.when ?? "";
  const n = parseInt(w, 10) || 0;
  if (/min/.test(w)) return n;
  if (/godz/.test(w)) return n * 60;
  if (/wczoraj/.test(w)) return 1440;
  if (/dni/.test(w)) return n * 1440;
  return Number.MAX_SAFE_INTEGER;
};

// Sortowanie kafli: najpierw przypięte, dalej chronologicznie od najmłodszych.
const cmpItems = (a: SampleItem, b: SampleItem): number =>
  (b.reason === "pin" ? 1 : 0) - (a.reason === "pin" ? 1 : 0) ||
  ageMinOf(a) - ageMinOf(b);

// Kafle prawej kolumny — można je maksymalizować na całą wysokość kolumny.
const RIGHT_KEYS = new Set(["mail", "chat", "notif"]);

interface GroupDef {
  key: string;
  title: string;
  icon: IconDefinition;
  accent: string;
  count: number;
  items: SampleItem[];
  span: string;
}

const GROUPS: GroupDef[] = [
  {
    key: "docs",
    title: "Teczki, foldery, pliki i linki",
    icon: faFolderOpen,
    accent: "#5d49c7",
    count: 10,
    span: styles.posDocs,
    items: [
      { icon: faFolderOpen, title: "Umowa najmu — Kowalski", sub: "Teczka · 12 dokumentów", reason: "pin" },
      { icon: faFile, title: "Decyzja_2026_114.pdf", sub: "Plik · 240 KB", reason: "recent", when: "10 min temu" },
      { icon: faLink, title: "Portal e-Doręczeń", sub: "Link", reason: "pin" },
      { icon: faFolderOpen, title: "Sprawa budowlana 7/2026", sub: "Teczka · 4 dokumenty", reason: "recent", when: "wczoraj" },
      { icon: faFile, title: "Wniosek_WZ.docx", sub: "Plik · 88 KB", reason: "recent", when: "2 godz. temu" },
      { icon: faLink, title: "BIP — uchwały Rady Gminy", sub: "Link", reason: "recent", when: "3 dni temu" },
      { icon: faFolderOpen, title: "Korespondencja 2026", sub: "Folder · 28 plików", reason: "pin" },
      { icon: faFile, title: "Protokół_zebrania.pdf", sub: "Plik · 132 KB", reason: "recent", when: "4 godz. temu" },
      { icon: faLink, title: "ePUAP — skrzynka podawcza", sub: "Link", reason: "recent", when: "4 dni temu" },
      { icon: faFolderOpen, title: "Umowy zlecenia 2026", sub: "Folder · 9 plików", reason: "recent", when: "5 dni temu" },
    ],
  },
  {
    key: "mail",
    title: "Przesyłki",
    icon: faEnvelope,
    accent: "#1565c0",
    count: 3,
    span: styles.posMail,
    items: [
      { icon: faLock, title: "Przesyłka e-szyfrowana", sub: "Kancelaria Nowak", reason: "recent", when: "8 min temu", badge: "e-szyfr." },
      { icon: faEnvelope, title: "e-Doręczenie — US Kraków", sub: "Doręczono", reason: "pin", badge: "e-Doręcz." },
      { icon: faEnvelope, title: "Portal Sądowy — sygn. I C 220/26", sub: "Nowe pismo", reason: "recent", when: "1 godz. temu", badge: "PI" },
    ],
  },
  {
    key: "notes",
    title: "Notatki",
    icon: faStickyNote,
    accent: "#e6a100",
    count: 2,
    span: styles.posNotes,
    items: [
      { icon: faStickyNote, title: "Zadzwonić do mecenasa ws. terminu", reason: "pin" },
      { icon: faStickyNote, title: "Sprawdzić status e-Doręczenia", reason: "recent", when: "wczoraj" },
    ],
  },
  {
    key: "chat",
    title: "Czat",
    icon: faComments,
    accent: "#2e7d32",
    count: 2,
    span: styles.posChat,
    items: [
      { icon: faComments, title: "Zespół — Kadry", sub: "Anna: „Przygotuję projekt…”", reason: "recent", when: "5 min temu" },
      { icon: faComments, title: "Mecenas Zając", sub: "3 nowe wiadomości", reason: "pin" },
    ],
  },
  {
    key: "notif",
    title: "Powiadomienia",
    icon: faBell,
    accent: "#c2255c",
    count: 3,
    span: styles.posNotif,
    items: [
      { icon: faBell, title: "Zaakceptowano fakturę FV/114", sub: "Budżet", reason: "recent", when: "12 min temu" },
      { icon: faBell, title: "Nowy dokument do podpisu", sub: "Akceptacja i Podpis", reason: "recent", when: "30 min temu" },
      { icon: faBell, title: "Przypomnienie: termin rozprawy", sub: "Jutro, 09:00", reason: "pin" },
    ],
  },
];

const DeskModule = () => {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const { modals, setModals } = useContext(ModalsManagerContext);
  // Biurko otwiera się domyślnie z aktywnym slajdem „Terminarz i zadania".
  const [plannerOpen, setPlannerOpen] = useState(true);
  // Maksymalizacja kafla prawej kolumny (Przesyłki/Czat/Powiadomienia) na całą
  // wysokość kolumny — chowa pozostałe dwa.
  const [maxCard, setMaxCard] = useState<string | null>(null);
  // Wybrana maska (motyw) Biurka — zapamiętana w localStorage.
  const [mask, setMask] = useState<string>(
    () => localStorage.getItem(MASK_STORAGE_KEY) || "current",
  );
  const [maskOpen, setMaskOpen] = useState(false);
  const chooseMask = (key: string) => {
    setMask(key);
    try {
      localStorage.setItem(MASK_STORAGE_KEY, key);
    } catch {
      /* brak dostępu do localStorage — ignorujemy */
    }
    setMaskOpen(false);
  };
  const maskClass =
    mask === "minimal"
      ? styles.maskMinimal
      : mask === "vivid"
        ? styles.maskVivid
        : mask === "onyx"
          ? styles.maskOnyx
          : "";

  const today = new Date().toLocaleDateString("pl-PL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className={`${styles.wrapper} ${maskClass}`}>
      {/* Hero */}
      <div className={styles.hero}>
        <div className={styles.heroLeft}>
          <div className={styles.heroTitleRow}>
            <div className={styles.heroTitle}>Biurko</div>
            {/* Wybór maski (motywu) — przy etykiecie Biurko, bo dotyczy całego widoku */}
            <div className={styles.maskPicker}>
              <button
                className={styles.maskBtn}
                onClick={() => setMaskOpen((o) => !o)}
                title="Zmień wygląd biurka i terminarza"
              >
                <FontAwesomeIcon icon={faPalette} />
                <span>Wygląd</span>
              </button>
              {maskOpen && (
                <>
                  <div className={styles.maskBackdrop} onClick={() => setMaskOpen(false)} />
                  <div className={styles.maskMenu}>
                    <div className={styles.maskMenuTitle}>Wygląd biurka i terminarza</div>
                    {MASKS.map((m) => (
                      <button
                        key={m.key}
                        className={`${styles.maskOption} ${mask === m.key ? styles.maskOptionActive : ""}`}
                        onClick={() => chooseMask(m.key)}
                      >
                        <span className={styles.maskSwatch}>
                          {m.swatch.map((c, i) => (
                            <span key={i} style={{ background: c }} />
                          ))}
                        </span>
                        <span className={styles.maskOptionText}>
                          <span className={styles.maskOptionName}>{m.name}</span>
                          <span className={styles.maskOptionDesc}>{m.desc}</span>
                        </span>
                        {mask === m.key && (
                          <FontAwesomeIcon icon={faCheck} className={styles.maskCheck} />
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className={styles.heroSub}>
            Przypięte i ostatnio używane — w jednym miejscu
          </div>
        </div>
        <div className={styles.heroRight}>
          <div className={styles.heroDate}>
            <button
              className={styles.heroPlannerBtn}
              onClick={() => setPlannerOpen(true)}
              title="Terminarz i zadania"
            >
              <FontAwesomeIcon icon={faCalendarDays} />
              <span>Terminarz</span>
            </button>
            <span>{today}</span>
          </div>
        </div>
      </div>

      <DeskPlanner open={plannerOpen} onClose={() => setPlannerOpen(false)} />

      {/* Bento */}
      <div className={styles.bento}>
        {GROUPS.map((g) => {
          const isMax = maxCard === g.key;
          const maxCol = maxCard ? (RIGHT_KEYS.has(maxCard) ? "R" : "L") : null;
          const myCol = RIGHT_KEYS.has(g.key) ? "R" : "L";
          // ukryj rodzeństwo TYLKO w tej samej kolumnie co zmaksymalizowany kafel
          if (maxCard !== null && maxCol === myCol && !isMax) return null;
          return (
            <section
              key={g.key}
              className={`${styles.card} ${g.span} ${isMax ? styles.maxed : ""}`}
              style={{ ["--accent" as string]: g.accent }}
            >
              <header className={styles.cardHead}>
                <span className={styles.cardIcon}>
                  <FontAwesomeIcon icon={g.icon} />
                </span>
                <span className={styles.cardTitle}>{g.title}</span>
                <span className={styles.cardCount}>{g.count}</span>
                <button
                  className={styles.cardExpand}
                  title={isMax ? "Przywróć układ" : "Rozwiń na całą kolumnę"}
                  onClick={() => setMaxCard(isMax ? null : g.key)}
                >
                  <FontAwesomeIcon icon={isMax ? faCompress : faExpand} />
                </button>
              </header>
              <div className={styles.cardBody}>
                {[...g.items].sort(cmpItems).map((it, i) => (
                  <ItemTile key={i} item={it} />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {isMobile && (
        <Paper
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            borderRadius: "0 !important",
          }}
          elevation={3}
        >
          <BottomNavigation
            sx={{ bgcolor: "#fdf8fd" }}
            onChange={(event, newValue) => {
              if (newValue === 0) setModals({ ...modals, menu: true });
            }}
          >
            <BottomNavigationAction title="Menu" icon={<MenuIcon />} />
            <BottomNavigationAction title="" icon={<></>} />
            <BottomNavigationAction title="" icon={<></>} />
            <BottomNavigationAction title="" icon={<></>} />
          </BottomNavigation>
        </Paper>
      )}
    </div>
  );
};

export default DeskModule;
